"use client";
import useSwr from "swr";
import {
  getInitiativeClassificationList,
  getOrganizationListForForm,
} from "@/cms-helpers/api-helper.client";
import { I18nLocale } from "@/i18nConfig.server";
import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { ValidatorFormProps } from "@cc/validation-form";
import { startCase } from "lodash";
import { KeyboardArrowDown } from "@mui/icons-material";
import { MenuItem } from "@mui/material";
import {
  IsEmail,
  IsNotEmpty,
  IsOptional,
  IsPhoneNumber,
  Matches,
  ValidateIf,
} from "class-validator";
import { UseFormReturn } from "react-hook-form";
import axios from "axios";

export interface UseCompanyAndCategoryOptions {
  locale: I18nLocale;
  formMethods?: UseFormReturn<any>;
}

export const useCompanyAndCategory = (props: UseCompanyAndCategoryOptions) => {
  const { locale, formMethods } = props;
  const [company, setCompany] = useState("");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const { data: CompaniesData, isLoading: companiesLoading } = useSwr(
    "contact-us-companies",
    () => getOrganizationListForForm(locale),
  );
  const { data: classificationData, isLoading: classificationLoading } = useSwr(
    company,
    (company) => getInitiativeClassificationList(locale, company),
  );

  const companies = useMemo(() => {
    const companies = (CompaniesData || []).map((item) => ({
      name: item.title,
      value: item.slug,
    }));
    companies.unshift({
      name:
        companies.length > 0
          ? "Select Bajaj Company"
          : companiesLoading
            ? "Loading..."
            : "No companies found",
      value: "",
    });
    return companies;
  }, [CompaniesData, companiesLoading]);

  const classifications = useMemo(() => {
    const classifications = (classificationData || []).map((item) => ({
      name: item.title,
      value: item.slug,
    }));
    classifications.unshift({
      name:
        classifications.length > 0
          ? "Select Initiative Category (optional)"
          : classificationLoading
            ? "Loading..."
            : "Select Initiative Category (optional)",
      value: "",
    });
    return classifications;
  }, [classificationData, classificationLoading]);

  const inputFields = useMemo<
    ValidatorFormProps<ContactUsFormDTO>["inputFields"]
  >(
    () => [
      {
        name: "name",
        placeholder: "Name",
        ItemComponentProps: {
          md: 6,
        },
      },
      {
        name: "email",
        placeholder: "Email ID",
        ItemComponentProps: {
          md: 6,
        },
      },
      {
        name: "phone",
        placeholder: "Phone Number",
        ItemComponentProps: {
          md: 6,
        },
      },
      {
        name: "organization",
        placeholder: "Organization Name",
        ItemComponentProps: {
          md: 6,
        },
      },
      {
        name: "company",
        select: true,
        color: "secondary",
        ItemComponentProps: {
          md: 6,
        },
        SelectProps: {
          renderValue: (value: unknown) => {
            const company = companies.find((item) => item.value === value);
            return startCase(company?.name as string) as ReactNode;
          },
          displayEmpty: true,
          IconComponent: KeyboardArrowDown,
        },
        children: companies.map((item, idx) => {
          const { name, value } = item;
          return (
            <MenuItem
              key={`${name}-${value}-${idx}`}
              value={value}
              sx={{ py: 2 }}
            >
              {startCase(name)}
            </MenuItem>
          );
        }),
      },
      {
        name: "category",
        select: true,
        color: "secondary",
        disabled: company === "",
        ItemComponentProps: {
          md: 6,
        },
        SelectProps: {
          renderValue: (value: unknown) => {
            const classification = classifications.find(
              (item) => item.value === value,
            );
            return startCase(classification?.name as string) as ReactNode;
          },
          displayEmpty: true,
          IconComponent: KeyboardArrowDown,
        },
        children: classifications.map((item, idx) => {
          const { name, value } = item;
          return (
            <MenuItem
              key={`${name}-${value}-${idx}`}
              value={value}
              sx={{ py: 2 }}
            >
              {startCase(name)}
            </MenuItem>
          );
        }),
      },
      {
        name: "message",
        placeholder: "Enter A Message",
        multiline: true,
        rows: 4,
        sx: {
          "textarea::placeholder": {
            opacity: 0.6,
          },
        },
      },
    ],
    [classifications, companies, company],
  );

  const handleSubmit = useCallback(
    (data: any) => {
      axios("/api/send-mail/home", {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        data: JSON.stringify(data),
      })
        .then(() => {
          setIsSuccess(true);
          formMethods?.reset();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [formMethods],
  );

  return {
    companies,
    classifications,
    company,
    setCompany,
    inputFields,
    handleSubmit,
    isSuccess,
    setIsSuccess,
  };
};

export class ContactUsFormDTO {
  @IsNotEmpty()
  @Matches(/^[A-Za-z\s]+$/, {
    message: "Name can only contain alphabets and spaces",
  })
  name: string;
  @IsEmail()
  @IsNotEmpty()
  email: string;

  @IsPhoneNumber("IN")
  @IsNotEmpty()
  phone: string;

  @IsNotEmpty()
  @Matches(/^(?![0-9]+$)[a-zA-Z0-9\s]+$/, {
    message: "Invalid organization name",
  })
  organization: string;

  @IsNotEmpty()
  company: string;
  @ValidateIf((o) => !!o.company)
  @IsOptional()
  category: string;
  @IsNotEmpty()
  message: string;
}
